import AccessKey from "./keysTree";
import { AccessTranslate } from "./interface";

/**
 * `ACCESS_TRANSLATE` is an object that maps each `AccessKey` to its corresponding translation key.
 * This object serves as a reference for translating branch names in the access tree structure.
 * Each property points to the appropriate translation string for that access level or page, ensuring
 * correct labeling of access paths within the application.
 */
const ACCESS_TRANSLATE: AccessTranslate = {
	[AccessKey.SHOW]: "show",
	[AccessKey.MAIN]: "routes.main",
	[AccessKey.LOGIN]: "routes.loginPage",
	[AccessKey.ORDERS]: "mainPage.orders.title",
	[AccessKey.ACCOUNTING]: "mainPage.accounting.title",
	[AccessKey.ACCOUNTS]: "mainPage.accounts.title",
	[AccessKey.ARCHIVES]: "mainPage.archives.title",
	[AccessKey.RATINGS]: "mainPage.ratings.title",
	[AccessKey.RATINGS_EXECUTORS]: "mainPage.ratings.executors.title",
	[AccessKey.REPORTS]: "mainPage.reports.title",
	[AccessKey.REPORTS_CHARTS]: "mainPage.reports.charts.title",
	[AccessKey.REPORTS_ACCOUNTING]: "mainPage.reports.accounting.title",
	[AccessKey.REPORTS_SALARY]: "mainPage.reports.salary.title",
	[AccessKey.CUSTOMERS]: "mainPage.customers.title",
	[AccessKey.BLACKLIST]: "mainPage.blacklist.title",
	[AccessKey.PREFERENCES_PAGE_NAME]: "mainPage.preferences.title",
	[AccessKey.SETTINGS]: "settings.title",

	[AccessKey.SETTINGS_ORDERS]: "settings.orders.title",
	[AccessKey.SETTINGS_TARIFFS]: "settings.tariffs.title",
	[AccessKey.SETTINGS_FINANCES]: "settings.finances.title",
	[AccessKey.SETTINGS_MESSAGES]: "settings.messages.title",
	[AccessKey.SETTINGS_MOBILE_APPS]: "settings.mobileApps.title",
	[AccessKey.SETTINGS_PARAMETERS]: "settings.parameters.title",
	[AccessKey.SETTINGS_MAP]: "settings.map.title",
	[AccessKey.SETTINGS_ORDER_EXCHANGE]: "settings.orderExchange.title",
	[AccessKey.SETTINGS_PAYMENT_SYSTEMS]: "settings.paymentSystems.title",
	[AccessKey.SETTINGS_RECEIPT_PROVIDERS]: "settings.receiptProviders.title",
	[AccessKey.SETTINGS_INTERFACE]: "settings.interface.title",
	[AccessKey.SETTINGS_VOIP]: "settings.voip.title",
	[AccessKey.SETTINGS_CAR_PARKS]: "settings.carParks.title",
	[AccessKey.SETTINGS_SYSTEM_PERFORMANCE]:
		"settings.systemPerformance.title",
	[AccessKey.SYSTEM_PERFORMANCE_OVERVIEW]:
		"settings.systemPerformance.tabs.overview",
	[AccessKey.SYSTEM_PERFORMANCE_INCIDENTS]:
		"settings.systemPerformance.tabs.incidents",
	[AccessKey.SYSTEM_PERFORMANCE_OPERATIONS]:
		"settings.systemPerformance.tabs.operations",
	[AccessKey.SYSTEM_PERFORMANCE_CLIENTS]:
		"settings.systemPerformance.tabs.clients",

	[AccessKey.EXECUTORS]: "mainPage.accounts.executors.title",
	[AccessKey.TRANSPORT]: "mainPage.accounts.transport.title",
	[AccessKey.USERS]: "mainPage.accounts.users.title",
	[AccessKey.ROLES]: "mainPage.accounts.roles.title",
	[AccessKey.CAR_PARKS]: "mainPage.accounts.carParks.title",
	[AccessKey.SCHEDULES]: "mainPage.accounts.schedules.title",
	[AccessKey.EXECUTOR_TEAMS]: "mainPage.accounts.executorTeams.title",

	[AccessKey.CLIENTS]: "mainPage.customers.clients.title",
	[AccessKey.COUNTERPARTIES]: "mainPage.customers.counterparties.title",
	[AccessKey.AGENTS]: "mainPage.customers.agents.title",
	[AccessKey.CLIENT_GROUPS]: "mainPage.customers.clientGroups.title",
	[AccessKey.COUNTERPARTY_GROUPS]:
		"mainPage.customers.counterpartyGroups.title",
	[AccessKey.AGENT_GROUPS]: "mainPage.customers.agentGroups.title",

	[AccessKey.BLACKLIST_CUSTOMERS]: "mainPage.blacklist.customers.title",
	[AccessKey.BLACKLIST_EXECUTORS]: "mainPage.blacklist.executors.title",
	[AccessKey.BLACKLIST_TRANSPORT]: "mainPage.blacklist.transport.title",

	[AccessKey.BANK_TRANSACTIONS]: "mainPage.archives.bankTransactions.title",
	[AccessKey.TRANSACTIONS]: "mainPage.archives.transactions.title",
	[AccessKey.RATING_CHANGES]: "utils.getTabsModeOne.ratingChanges",

	[AccessKey.GPS_LOG]: "mainPage.archives.gpsLog.title",
	[AccessKey.MESSAGES]: "mainPage.archives.messages.title",
	[AccessKey.PUSH]: "mainPage.archives.push.title",

	[AccessKey.CALL_HISTORY]: "mainPage.archives.callsHistory.title",
	[AccessKey.RECEIPTS_HISTORY]: "mainPage.archives.receiptsHistory.title",

	[AccessKey.EXECUTORS_GENERAL]: "mainPage.accounting.executorsGeneral.title",
	[AccessKey.BANK_ACCOUNTS]: "mainPage.accounting.bankAccounts.title",
	[AccessKey.REPLENISH_PERFORMERS_BALANCE]:
		"mainPage.accounting.replenishPerformersBalance.title",

	[AccessKey.GENERAL]: "mainPage.reports.charts.byOrders.title",

	[AccessKey.CASHLESS_ORDERS]:
		"mainPage.reports.accounting.cashlessOrders.title",
	[AccessKey.CASHLESS_ORDERS_GENERAL]:
		"mainPage.reports.accounting.cashlessOrdersGeneral.title",
	[AccessKey.CASHLESS_ORDERS_PERIOD_REPORT]:
		"mainPage.reports.accounting.cashlessOrdersPeriodReport.title",
	[AccessKey.CASHLESS_ACCOUNTS_SUMMARY_REPORT]:
		"mainPage.reports.accounting.cashlessAccountsSummaryReport.title",
	[AccessKey.FUEL_DEDUCTION_ORDERS_REPORT]:
		"mainPage.reports.accounting.fuelDeductionOrdersReport.title",
	[AccessKey.AGENT_ORDERS_REPORT]:
		"mainPage.reports.accounting.agentOrdersReport.title",
	[AccessKey.ACCOUNTS_REPORT]:
		"mainPage.reports.accounting.accountsReport.title",

	[AccessKey.USERS_ACRIVITY]: "mainPage.reports.salary.usersActivity.title",

	[AccessKey.ORDERS_DISTRIBUTION]: "settings.ordersDistribution.title",
	[AccessKey.FEED_TIME_CALCULATION]: "settings.feedTimeCalculation.title",

	[AccessKey.MAIN_TARIFFS]: "settings.tariffs.main.title",
	[AccessKey.AUTOMATIC_TARIFFS]: "settings.tariffs.auto.title",
	[AccessKey.GENERAL_TARIFFS]: "settings.tariffs.general.title",
	[AccessKey.ADDITIONAL]: "settings.tariffs.additional.title",

	[AccessKey.EXECUTOR_TARIFF_PLANS]:
		"settings.finances.executorTariffPlans.title",
	[AccessKey.CUSTOMER_BALANCES]: "settings.finances.customerBalances.title",
	[AccessKey.CUSTOMER_TARIFF_PLANS]:
		"settings.finances.customerTariffPlans.title",
	[AccessKey.USER_TARIFF_PLANS]: "settings.finances.userTariffPlans.title",
	[AccessKey.EXECUTOR_PENALTIES]: "settings.finances.executorPenalties.title",
	[AccessKey.EXECUTOR_RATINGS]: "settings.finances.executorRatings.title",
	[AccessKey.CUSTOMER_PENALTIES]: "settings.finances.customerPenalties.title",
	[AccessKey.USER_PENALTIES]: "settings.finances.userPenalties.title",

	[AccessKey.PROVIDER_SMS]: "settings.messages.providerSms.title",
	[AccessKey.TEMPLATES_SMS]: "settings.messages.templatesSms.title",
	[AccessKey.PUSH_NOTICES]: "settings.messages.pushNotices.title",

	[AccessKey.MOBILE_APP_EXECUTOR]: "settings.mobileApps.executor.title",
	[AccessKey.MOBILE_APP_CLIENT]: "settings.mobileApps.client.title",

	[AccessKey.APPLICATION]: "settings.parameters.tabs.programs.title",

	[AccessKey.CUSTOMER]: "settings.paymentSystems.customer.title",
	[AccessKey.EXECUTOR]: "settings.paymentSystems.executor.title",
	[AccessKey.TERMINAL_EXECUTOR]: "settings.paymentSystems.terminalExecutor.title",

	[AccessKey.COLORS]: "settings.interface.colors.title",
	[AccessKey.FONT]: "settings.interface.fonts.title",
	[AccessKey.KEYBINDS]: "settings.interface.keybinds.title",

	[AccessKey.CAR_MODELS_AND_BRANDS]: "pages.preferencesPages.navInfo.str200",
	[AccessKey.CAR_CLASSES]: "pages.preferencesPages.navInfo.str201",
	[AccessKey.OBJECTS]: "pages.preferencesPages.navInfo.str204",
	[AccessKey.SECTORS]: "pages.preferencesPages.navInfo.str205",
	[AccessKey.PARKINGS]: "pages.preferencesPages.navInfo.str206",
	[AccessKey.PRICE_ZONES]: "pages.preferencesPages.navInfo.str207",
	[AccessKey.SERVICES]: "pages.preferencesPages.navInfo.str208",

	[AccessKey.CONTROL_BUTTON_ADD]: "table.buttons.add",
	[AccessKey.CONTROL_BUTTON_EDIT]: "table.buttons.edit",
	[AccessKey.CONTROL_BUTTON_DELETE]: "table.buttons.delete",

	[AccessKey.NEW_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str0",
	[AccessKey.EDIT_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str1",
	[AccessKey.REVERT_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str2",
	[AccessKey.CLOSED_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str3",
	[AccessKey.COPY_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str4",
	[AccessKey.SET_ORDER_EXECUTOR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str200",
	[AccessKey.APPROVE_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str201",
	[AccessKey.SEND_ORDER_AGAIN]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str5",
	[AccessKey.RECALCULATE_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str6",
	[AccessKey.RECALCULATE_ORDER_WITH_ACTUAL_RATES]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str7",
	[AccessKey.RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str202",
	[AccessKey.SEND_ORDER_TO_AIR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str203",
	[AccessKey.SEND_ORDER_TO_EXECUTING]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str204",
	[AccessKey.TOGGLE_ORDER_AIR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str205",
	[AccessKey.SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str8",
	[AccessKey.SET_ORDER_CALL_TO_CLIENT_SUCCESS]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str9",
	[AccessKey.SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str10",
	[AccessKey.SET_ORDER_CALL_TO_EXECUTOR_SUCCESS]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str11",
	[AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str13",
	[AccessKey.COLOR_ORDER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.colorButton.str0",
	[AccessKey.CALL_ORDER_CLIENT]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.callClientButton.str0",
	[AccessKey.CALL_ORDER_EXECUTOR]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.callExexcutorButton.str0",
	[AccessKey.SMS_BUTTON]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.sMSButton.str0",
	[AccessKey.AUTO_CALL_CLIENT_BUTTON]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.str12",

	[AccessKey.WIDGET_ORDER_PARKING]: "orderPage.rightMenu.toggle.parkings",
	[AccessKey.WIDGET_ORDER_EXECUTORS]: "orderPage.rightMenu.toggle.executors",
	[AccessKey.WIDGET_ORDER_MAP]: "orderPage.rightMenu.toggle.map",
	[AccessKey.WIDGET_ORDER_ORDER_DETAILS]:
		"orderPage.rightMenu.toggle.orderDetails",
	[AccessKey.WIDGET_ORDER_CHAT]: "orderPage.rightMenu.toggle.chat",

	[AccessKey.CALL]: "call",
	[AccessKey.SENDING_SMS]:
		"pages.mainPage.pages.customers.tabs.clients.header.sendingSMS.str201",
	[AccessKey.SENDEING_PUSH]:
		"pages.mainPage.pages.customers.tabs.clients.header.sendingPush.str201",
	[AccessKey.BALANSE]:
		"pages.mainPage.pages.customers.tabs.clients.header.str0",
	[AccessKey.BONUS_BALANSE]:
		"pages.mainPage.pages.customers.tabs.clients.header.str1",
	[AccessKey.IMPORT]: "import",
	[AccessKey.EXPORT]: "export",
	[AccessKey.REVERT_ORDER_TO_ARCHIVE]: "change_history.actions.revert",
	[AccessKey.FILTER]: "filter.title",
	[AccessKey.REFRESH]: "refresh",
	[AccessKey.BALANCE_CHANGE]: "balanceModal.str200",

	[AccessKey.LIVE_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.hooks.str0",
	[AccessKey.PRELIMINARY_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.hooks.str1",
	[AccessKey.EXECUTING_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.hooks.str2",
	[AccessKey.ALL_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.hooks.str3",
	[AccessKey.CLOSED_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.hooks.str4",

	[AccessKey.AFTER_ADDITIONAL_BUTTONS]: "afterAdditionalButtons",

	[AccessKey.METADATA]: "metadata.title",
	[AccessKey.MENU]: "metadata.menu",
	[AccessKey.PAGES]: "metadata.pages",
	[AccessKey.TABS]: "metadata.tabs",
	[AccessKey.BUTTONS]: "metadata.buttons",
	[AccessKey.WIDGETS]: "metadata.widgets",
	[AccessKey.MODALS]: "metadata.modals",
	[AccessKey.FIELDS]: "metadata.fields",
	[AccessKey.CHECK_BOXS]: "metadata.checkBoxs",
	[AccessKey.CARD_ORDERS]: "metadata.cardOrders",
	[AccessKey.SELECTOR]: "metadata.selector",
	[AccessKey.EDITORS]: "metadata.editors",
	[AccessKey.RADIO_BUTTONS]: "metadata.radioButtons",
	[AccessKey.PERMISSION]: "metadata.permission",
	[AccessKey.PERFORMER_CARD]: "metadata.performerCard",
	[AccessKey.TRANSPORT_CARD]: "metadata.transportCard",
	[AccessKey.BLOCKS]: "metadata.blocks",

	[AccessKey.SEARCH_CAR]: "settings.interface.keybinds.keys.searchCar",
	[AccessKey.SEARCH_EXECUTOR]:
		"settings.interface.keybinds.keys.searchExecutor",
	[AccessKey.SEARCH_ORDER]: "settings.interface.keybinds.keys.searchOrder",

	[AccessKey.ADD_POINT]: "orderPageWidgets.orderDetails.controls.str0",
	[AccessKey.INSERT_POINT]: "orderPageWidgets.orderDetails.controls.str1",
	[AccessKey.EDIT_POINT]: "orderPageWidgets.orderDetails.controls.str2",
	[AccessKey.DELETE_POINT]: "orderPageWidgets.orderDetails.controls.str3",
	[AccessKey.MOVE_POINT_UP]: "orderPageWidgets.orderDetails.controls.str4",
	[AccessKey.MOVE_POINT_DOWN]: "orderPageWidgets.orderDetails.controls.str5",

	[AccessKey.ORDER_MODAL]: "orderModal",

	[AccessKey.SHOW_ACTIVE_CLIENT]:
		"pages.mainPage.pages.customers.tabs.clients.header.str203",
	[AccessKey.SHOW_NO_ACTIVE_CLIENT]:
		"pages.mainPage.pages.customers.tabs.clients.header.str204",
	[AccessKey.SHOW_ACTIVE_COUNTERPARTIES]:
		"mainPage.customers.counterparties.table.showActive",
	[AccessKey.SHOW_NO_ACTIVE_COUNTERPARTIES]:
		"mainPage.customers.counterparties.table.showNoActive",

	[AccessKey.ARCHIVE_FILTER_EXECUTOR]:
		"pages.mainPage.pages.archives.tabs.orders.filter.executor",
	[AccessKey.ARCHIVE_FILTER_STATUS_ORDER]:
		"pages.mainPage.pages.archives.tabs.orders.filter.statusOrder",
	[AccessKey.ARCHIVE_FILTER_NUMBER_ORDER]:
		"pages.mainPage.pages.archives.tabs.orders.filter.numberOrder",
	[AccessKey.ARCHIVE_FILTER_NUMBER_PHONE]:
		"pages.mainPage.pages.archives.tabs.orders.filter.numberPhone",
	[AccessKey.ARCHIVE_FILTER_STREET]:
		"pages.mainPage.pages.archives.tabs.orders.filter.street",
	[AccessKey.ARCHIVE_FILTER_CLIENT]:
		"pages.mainPage.pages.archives.tabs.orders.filter.client",
	[AccessKey.ARCHIVE_FILTER_COUNTERPARTY]:
		"pages.mainPage.pages.archives.tabs.orders.filter.counterparty",
	[AccessKey.ARCHIVE_FILTER_AGENT]:
		"pages.mainPage.pages.archives.tabs.orders.filter.agent",
	[AccessKey.ARCHIVE_FILTER_PAYMENTS]:
		"pages.mainPage.pages.archives.tabs.orders.filter.payments",
	[AccessKey.ARCHIVE_FILTER_CLASSES]:
		"pages.mainPage.pages.archives.tabs.orders.filter.classes",
	[AccessKey.ARCHIVE_FILTER_SERVICES]:
		"pages.mainPage.pages.archives.tabs.orders.filter.services",
	[AccessKey.ARCHIVE_FILTER_SECTORS]:
		"pages.mainPage.pages.archives.tabs.orders.filter.sectors",
	[AccessKey.ARCHIVE_FILTER_TRANSFER]:
		"pages.mainPage.pages.archives.tabs.orders.filter.transfer",
	[AccessKey.ARCHIVE_FILTER_SOURCE_CALL_SIGN]:
		"pages.mainPage.pages.archives.tabs.orders.filter.customerCallSignCreated",

	[AccessKey.ORDER_RETURN_FROM_TRANSFER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.orderReturnFromTransfer",
	[AccessKey.ORDER_SEND_TO_TRANSFER]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.orderSendToTransfer",
	[AccessKey.ORDER_SEND_TO_TRANSFER_LOCAL]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.orderSendToTransferLocal",
	[AccessKey.ORDER_SEND_TO_TRANSFER_GLOBAL]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.orderSendToTransferGlobal",
	[AccessKey.ORDER_SEND_TO_TRANSFER_OUTSIDE]:
		"pages.mainPage.pages.orders.orderHeader.secondRow.orderSendToTransferOutside",

	[AccessKey.SHOW_ONLY_MY_ORDERS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.str0",
	[AccessKey.SHOW_ORDERS_WITHOUT_EXECUTORS]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.str1",
	[AccessKey.FILTER_ORDERS_BY_MULTY]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterMulty",
	[AccessKey.GPS_SPOOFING]: "gpsSpoofingButton.title",
	[AccessKey.PAYMENT_LINK_SMS]: "paymentLinkSms.title",

	[AccessKey.RELEVANCE]: "orderPageWidgets.chat.scheduleModal.header.str100",
	[AccessKey.CHOISE_CUSTOMERS]:
		"orderPageWidgets.chat.usersModal.header.str130",
	[AccessKey.SEND_SMS]: "orderPageWidgets.chat.sendBar.btnText",

	[AccessKey.CHANGE_PERFORMERS_STATUS]:
		"orderPageWidgets.executors.executorModal.str0",
	[AccessKey.CHANGE_PERFORMERS_STATUS_SELECTED]:
		"accessText.changeOfPerformerStatusSelected",
	[AccessKey.SEARCH]: "accessText.search",
	[AccessKey.LOWERS_STATISTICS_TABLE]: "accessText.lowerStatisticsTable",

	[AccessKey.MAP_FILTER_POINTS]: "orderPageWidgets.map2.showButtons.str0",
	[AccessKey.MAP_FILTER_PRICE_ZONE]: "orderPageWidgets.map2.showButtons.str1",
	[AccessKey.MAP_FILTER_SECTOR]: "orderPageWidgets.map2.showButtons.str2",
	[AccessKey.MAP_FILTER_PARKING]: "orderPageWidgets.map2.showButtons.str3",
	[AccessKey.MAP_FILTER_EXECUTOR]: "orderPageWidgets.map2.showButtons.str4",
	[AccessKey.MAP_FILTER_CLIENT]: "orderPageWidgets.map2.showButtons.str5",

	[AccessKey.REPLY_TO_A_MESSAGE]:
		"orderPageWidgets.chat.chatList.contextMenu.str100",
	[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
		"accessText.companiesAndTaxiServices",
	[AccessKey.STATUS]: "orderPageWidgets.executors.columns.status",
	[AccessKey.IS_WORKING]: "orderPageWidgets.executors.columns.isWorking",
	[AccessKey.BY_ORDER]:
		"orderPageWidgets.map2.header.filters.content.form.str150",
	[AccessKey.PARKING_GROUP_LIST]:
		"orderPageWidgets.parking.filter.popover.content.str4",

	[AccessKey.OUT_OF_TOWN_DISTANCE]:
		"orderPageWidgets.orderDetails.info.str200",
	[AccessKey.IDLE_TIME]: "orderPageWidgets.orderDetails.info.str11",
	[AccessKey.PRICE]: "orderPageWidgets.orderDetails.info.str13",
	[AccessKey.ADDITIONAL_PRICE]: "orderPageWidgets.orderDetails.info.str209",
	[AccessKey.AWAITING_TIME]: "orderPageWidgets.orderDetails.info.str15",
	[AccessKey.HOURLY_TIME]: "orderPageWidgets.orderDetails.info.str17",

	[AccessKey.PHONE]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterOrdersModal.content.str0",
	[AccessKey.FROM]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterOrdersModal.content.str1",
	[AccessKey.TO]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterOrdersModal.content.str2",

	[AccessKey.FLEET_NUMBER]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterOrdersByCarModal.content.str200",
	[AccessKey.LINCENSE_PLATE]:
		"pages.mainPage.pages.orders.orderHeader.firstRow.filterOrdersByCarModal.content.str201",

	[AccessKey.MAP]: "mainPage.archives.orders.modal.tabs.map",
	[AccessKey.EVENTS]: "mainPage.archives.orders.modal.tabs.events",
	[AccessKey.CONVERSATIONS]:
		"mainPage.archives.orders.modal.tabs.conversations",

	[AccessKey.ROUTE_MAP]: "accessText.routeMap",
	[AccessKey.PRICE_ZONES_MAP]: "accessText.priceZonesMap",
	[AccessKey.SECTORS_MAP]: "accessText.sectorsMap",
	[AccessKey.PARKINGS_MAP]: "accessText.parkingsMap",
	[AccessKey.DRIVER_MAP]: "accessText.driverMap",
	[AccessKey.CLIENT_MAP]: "accessText.clientMap",
	[AccessKey.CENTER_MAP]: "accessText.centerMap",
	[AccessKey.TRACK_MAP]: "accessText.trackMap",

	[AccessKey.STREET]:
		"pages.mainPage.pages.orders.orderModal.footer.searchFilter.str0",
	[AccessKey.LOCAL_OBJECT]:
		"pages.mainPage.pages.orders.orderModal.footer.searchFilter.str1",
	[AccessKey.OBJECT]:
		"pages.mainPage.pages.orders.orderModal.footer.searchFilter.str2",
	[AccessKey.CLOSE_ALL]: "close_all",
	[AccessKey.CANCEL]: "cancel",
	[AccessKey.SAVE]: "save",

	[AccessKey.AUTO_SUPPLY]:
		"pages.mainPage.pages.orders.orderModal.orderRideInformation.str209",
	[AccessKey.OUT_OF_TOWN]: "orderPage.order_form.intercity_road_trip",
	[AccessKey.SUPPLY]: "orderPage.order_form.is_feed",
	[AccessKey.HOURLY]: "orderPage.order_form.hourly",
	[AccessKey.TAXIMETER]:
		"pages.mainPage.pages.orders.orderModal.orderRideInformation.str207",

	[AccessKey.DISCOUNT_PLAN]: "discount.discountPlan",
	[AccessKey.BONUS_TRIP]: "discount.bonusTrip",
	[AccessKey.BONUS_BALANCE]: "discount.bonusBalance",
	[AccessKey.ACCUMULATIVE_DISCOUNT]: "discount.useDiscount",
	[AccessKey.MAX_BONUSES_AND_INPUT]: "discount.useBonusAndInput",

	[AccessKey.ORDER_TIME]: "orderPage.order_form.order_time.time_select",
	[AccessKey.PAYMENT_TYPE]: "accessText.paymentType",
	[AccessKey.TARIFF]: "accessText.tariff",
	[AccessKey.PASSENGER_COUNT]: "accessText.passengerCount",

	[AccessKey.PERSONAL_INFORMATION]: "accessText.personalInformation",
	[AccessKey.POINT_TABLE]: "accessText.pointsTable",

	[AccessKey.TRANSPORTATION_TYPE]: "accessText.transportationType",
	[AccessKey.ADD_NEW_ORDER]: "accessText.addNewOrder",

	[AccessKey.VIEW]: "accessText.view",
	[AccessKey.EDIT]: "accessText.edit",

	[AccessKey.ORDER_COUNT]:
		"mainPage.customers.clients.modalFilter.numberTrack",
	[AccessKey.ONLY_MOBILE_APP]:
		"mainPage.customers.clients.modalFilter.onlyMobApp",

	[AccessKey.CAR_OPTIONS]: "accessText.carOptions",
	[AccessKey.FAVORITES_AND_NON_FAVORITES]:
		"pages.preferencesPages.screenDirectory.carModelsAndBrands.header.filters.popover.content.str209",
	[AccessKey.ACTIVE_AND_INACTIVE]:
		"pages.preferencesPages.screenDirectory.carModelsAndBrands.header.filters.popover.content.str210",
	[AccessKey.BASE_AND_CREATED]:
		"pages.preferencesPages.screenDirectory.carModelsAndBrands.header.filters.popover.content.str211",

	[AccessKey.TABLE_NAVIGATION]: "accessText.tableNavigation",
	[AccessKey.LANGUAGE]: "accessText.language",

	[AccessKey.OBJECTS_AND_OBJECT_GROUPS]: "accessText.objectsAndObjectGroups",
	[AccessKey.SHOW_ALL]:
		"pages.preferencesPages.screenDirectory.objects.header.filters.popover.content.str130",
	[AccessKey.SHOW_OBJECTS]:
		"pages.preferencesPages.screenDirectory.objects.header.filters.popover.content.str131",
	[AccessKey.SHOW_STREETS]:
		"pages.preferencesPages.screenDirectory.objects.header.filters.popover.content.str132",
	[AccessKey.HIDDEN_IN_CLIENT_APP]:
		"pages.preferencesPages.screenDirectory.objects.header.filters.popover.content.str133",
	[AccessKey.HIDE_INACTIVE]:
		"pages.preferencesPages.screenDirectory.objects.header.filters.popover.content.str134",

	[AccessKey.FORM_REPORT_AND_FILTER]: "accessText.formReportAndFilter",
	[AccessKey.GROUP_ACTION]: "accessText.groupAction",
	[AccessKey.SAVE_AS_FILE]: "accessText.saveAsFile",
	[AccessKey.PRINT]: "accessText.print",

	[AccessKey.DATE]: "accessText.date",
	[AccessKey.GENERATE]: "accessText.generate",
	[AccessKey.CHART_TYPE]: "accessText.chartType",
	[AccessKey.TIME_TYPE]: "accessText.timeType",
	[AccessKey.ADD_ONLY_IF_OPERATIONS_IN_PERIOD]:
		"mainPage.reports.accounting.cashlessOrdersPeriodReport.model.checkBox",
	[AccessKey.ONLY_SUCCESSFUL]: "accessText.onlySuccessful",
	[AccessKey.CONTRACTOR]: "accessText.contractor",
	[AccessKey.CARDS]: "accessText.cards",
	[AccessKey.MAGNIFYING_GLASS]: "accessText.magnifyingGlass",
	[AccessKey.TYPES]: "accessText.types",
	[AccessKey.STATUSES]: "accessText.statuses",
	[AccessKey.FROM_OR_TO]:
		"pages.mainPage.pages.archives.tabs.transactions.header.filter.popover.str3",
	[AccessKey.TRANSACTION_NUMBER_INPUT]:
		"pages.mainPage.pages.archives.tabs.transactions.header.filter.popover.str1",

	[AccessKey.CALLSIGN]: "accessText.callsign",
	[AccessKey.PARK_NUMBER]: "accessText.parkNumber",

	[AccessKey.UPDATE]: "accessText.update",
	[AccessKey.PAYMENT_TYPE_NAME]: "accessText.paymentTypeName",
	[AccessKey.PAYMENT_METHOD]: "accessText.paymentMethod",

	[AccessKey.BALANCE_CHANGE_LOWER]: "accessText.balanceChangeLower",
	[AccessKey.UPDATE_LOWER]: "accessText.updateLower",
	[AccessKey.DATE_LOWER]: "accessText.dateLower",
	[AccessKey.VIEW_ENTRY_LOWER]: "accessText.viewEntryLower",
	[AccessKey.DRIVER_SUMMARY_LOWER]: "accessText.driverSummaryLower",

	[AccessKey.EDIT_CARD]: "accessText.editCard",
	[AccessKey.DELETE_CARD]: "accessText.deleteCard",
	[AccessKey.UPDATE_CARD]: "accessText.updateCard",
	[AccessKey.SHOW_ACTIVE_CARDS]: "accessText.showActiveCards",
	[AccessKey.SHOW_INACTIVE_CARDS]: "accessText.showInactiveCards",

	[AccessKey.BALANCE_CHANGE_SETTLEMENT]: "accessText.balanceChangeSettlement",
	[AccessKey.UPDATE_SETTLEMENT]: "accessText.updateSettlement",
	[AccessKey.DATE_SETTLEMENT]: "accessText.dateSettlement",

	// [AccessKey.BALANCE_CHANGE]: "accessText.balanceChange",
	[AccessKey.CALCULATE_FOR_DAY]: "accessText.calculateForDay",
	[AccessKey.ASSIGN_FINE]: "accessText.assignFine",
	[AccessKey.DRIVER_WORK_HISTORY]: "accessText.driverWorkHistory",

	[AccessKey.SHOW_ACTIVE]: "accessText.showActive",
	[AccessKey.SHOW_INACTIVE]: "accessText.showInactive",

	//
	// [AccessKey.MAIN]: "utils.getTabsModeOne.main",
	// [AccessKey.USER_SELECT]: "utils.getTabsModeOne.userSelect",
	// [AccessKey.EXECUTOR_SELECT]: "utils.getTabsModeOne.executorSelect",
	// [AccessKey.CAR_SELECT]: "utils.getTabsModeOne.carSelect",
	// [AccessKey.HISTORY]: "utils.getTabsModeOne.history",
	[AccessKey.OPTIONS]: "utils.getTabsModeOne.options",
	[AccessKey.ADDITIONAL_DATA]: "utils.getTabsModeOne.additionalData",
	[AccessKey.CARS]: "utils.getTabsModeOne.cars",
	[AccessKey.FILES]: "utils.getTabsModeOne.files",
	[AccessKey.COMMENTS]: "utils.getTabsModeOne.comments",
	// [AccessKey.ORDERS]: "utils.getTabsModeOne.orders",
	// [AccessKey.TRANSACTIONS]: "utils.getTabsModeOne.transactions",
	// [AccessKey.CUSTOMERS]: "utils.getTabsModeOne.customers",
	// [AccessKey.SCHEDULES]: "utils.getTabsModeOne.schedules",
	// [AccessKey.PASSPORT]: "utils.getTabsModeOne.passport",
	// [AccessKey.ACCESS]: "utils.getTabsModeOne.access",
	// [AccessKey.AVERAGE_SPEED]: "utils.getTabsModeOne.averageSpeed",
	// [AccessKey.PERMISSION_SELECT]: "utils.getTabsModeOne.permissionSelect",
	// [AccessKey.AUTOMATIC_FINES]: "utils.getTabsModeOne.automaticFines",
	// [AccessKey.MANUAL_FINES]: "utils.getTabsModeOne.manualFines",
	// [AccessKey.REWARD]: "utils.getTabsModeOne.reward",

	[AccessKey.RETURN]: "accessText.return",

	[AccessKey.PERFORMER_PHOTO]: "accessText.performerPhoto",
	[AccessKey.OTHER_FILES]: "accessText.otherFiles",
	[AccessKey.PASSPORT]: "accessText.passport",
	[AccessKey.TAX_ID]: "accessText.taxId",
	[AccessKey.DRIVER_LICENSE]: "accessText.driverLicense",
	[AccessKey.SOLE_PROPRIETOR]: "accessText.soleProprietor",
	[AccessKey.LICENSE]: "accessText.license",

	[AccessKey.SELECT_CAR]: "accessText.selectCar",
	[AccessKey.DETACH_CAR]: "accessText.detachCar",

	[AccessKey.PERSONAL_DATA]: "accessText.personalData",
	[AccessKey.ACCOUNT]: "accessText.account",
	[AccessKey.WORK_DATA]: "accessText.workData",
	[AccessKey.AVAILABLE_PAYMENT_METHODS]: "accessText.availablePaymentMethods",
	[AccessKey.MAIN_DATA]: "accessText.mainData",
	[AccessKey.PERMISSIONS]: "accessText.permissions",
	[AccessKey.BANK_CARD]: "accessText.bankCard",

	[AccessKey.NOTE]: "accessText.note",

	[AccessKey.LAST_NAME]: "accessText.lastName",
	[AccessKey.FIRST_NAME]: "accessText.firstName",
	[AccessKey.PATRONYMIC]: "accessText.patronymic",
	[AccessKey.WORK_PHONE]: "accessText.workPhone",
	// [AccessKey.LOGIN]: "accessText.login",
	[AccessKey.PASSWORD]: "accessText.password",
	[AccessKey.TIN]: "accessText.tin",
	[AccessKey.PRIORITY]: "accessText.priority",

	[AccessKey.COMPANY]: "accessText.company",
	[AccessKey.BRANCH]: "accessText.branch",
	[AccessKey.CALL_SIGN]: "accessText.callSign",
	[AccessKey.POSITIONS]: "accessText.positions",
	[AccessKey.RESPONSIBLE_EMPLOYEE]: "accessText.responsibleEmployee",
	[AccessKey.EXECUTOR_PENALTY]: "accessText.executorPenalty",
	[AccessKey.SHIFT_SCHEDULE]: "accessText.shiftSchedule",
	[AccessKey.PERFORMER_TEAM]: "accessText.performerTeam",
	[AccessKey.PERFORMER_TARIFF]: "accessText.performerTariff",

	[AccessKey.TECH_PASSPORT]: "accessText.techPassport",
	[AccessKey.INSURANCES]: "accessText.insurances",
	[AccessKey.LICENSE_CARD]: "accessText.licenseCard",
	[AccessKey.MAIN_DOCUMENTS]: "accessText.mainDocuments",
	[AccessKey.MAIN_INSURANCES]: "accessText.mainInsurances",
	[AccessKey.TRANSPORT_CLASSES_DISTRIBUTION]:
		"accessText.transportClassesDistribution",
	[AccessKey.TRANSPORT_CLASSES_BROADCAST]:
		"accessText.transportClassesBroadcast",
	//
	[AccessKey.PARKING_NUMBER]: "accessText.parkingNumber",
	[AccessKey.COLOR]: "accessText.color",
	[AccessKey.RELEASE_YEAR]: "accessText.releaseYear",
	[AccessKey.SEATS_COUNT]: "accessText.seatsCount",
	[AccessKey.BODY_NUMBER]: "accessText.bodyNumber",
	[AccessKey.TRANSPORT_TYPE]: "accessText.transportType",
	[AccessKey.TRANSPORT_BRAND]: "accessText.transportBrand",
	[AccessKey.TRANSPORT_MODEL]: "accessText.transportModel",
	[AccessKey.BODY_TYPE]: "accessText.bodyType",
	[AccessKey.TRANSPORT_CLASS]: "accessText.transportClass",
	[AccessKey.TRANSPORT_OWNERSHIP]: "accessText.transportOwnership",

	[AccessKey.ORDER_EXTENDED_FILTER]: "accessText.orderExtendedFilter",
	[AccessKey.REMOUNT_TRANSACTION_TYPE]:
		"pages.mainPage.pages.archives.tabs.bankTransactions.content.columns.remountTransactionType",
	[AccessKey.TRANSACTION_TYPE]:
		"pages.mainPage.pages.archives.tabs.bankTransactions.content.columns.transactionType",
};

export default ACCESS_TRANSLATE;
